/**
 * Merchant profile store — holds the human-readable store name (from
 * `MerchantSettings.merchantName`) so persistent chrome like the Sidebar can
 * show it without each screen re-fetching settings.
 *
 * Loaded once on connect (see App.tsx) and kept in sync when settings are saved
 * (see SettingsScreen). Degrades gracefully: if settings are unavailable the
 * name stays empty and callers fall back to the shop domain.
 */
import { create } from 'zustand';
import { fetchSettings } from '@/api/settings';

interface MerchantProfileState {
  merchantName: string;
  isLoaded: boolean;
  isLoading: boolean;
  /** Fetch settings once and cache the merchant name. Idempotent. */
  loadProfile: () => Promise<void>;
  /** Keep the name fresh after a settings save. */
  setMerchantName: (name: string) => void;
  reset: () => void;
}

export const useMerchantStore = create<MerchantProfileState>((set, get) => ({
  merchantName: '',
  isLoaded: false,
  isLoading: false,

  loadProfile: async () => {
    // Guard against duplicate/concurrent calls (e.g. StrictMode double-invoke):
    // if a request is already in flight or the profile is loaded, do nothing.
    if (get().isLoading || get().isLoaded) {
      return;
    }
    set({ isLoading: true });
    try {
      const settings = await fetchSettings();
      set({ merchantName: settings?.merchantName?.trim() ?? '', isLoaded: true });
    } catch {
      // Degrade gracefully — the Sidebar falls back to the shop domain.
      set({ isLoaded: true });
    } finally {
      set({ isLoading: false });
    }
  },

  setMerchantName: (name) => set({ merchantName: name.trim() }),

  reset: () => set({ merchantName: '', isLoaded: false, isLoading: false }),
}));
